import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Dialog — Radix's modal, wrapped.
 *
 * Radix owns the hard parts: the focus trap, returning focus to whatever opened it, Escape, the
 * inert background, and `aria-modal`. This wrapper owns the shape of a Clavian dialog, and makes
 * two things impossible rather than optional:
 *
 *   - `title` is required. A modal with no accessible name is the single most common dialog defect.
 *   - The close control is always there, always has a name, and the scrim is flat — no
 *     backdrop-filter, because the kiosk renders this over a running game (spec/12 §15).
 *
 * It comes in two shapes:
 *
 *   - **Compact** (no `size`): a confirm, a question, a short prompt — one padded column of 32rem that
 *     scrolls as a whole, and stays a card on a phone. The kiosk's full-screen stages are built on it.
 *   - **Framed** (`size` sm / md / lg / xl): a form or a detail the operator works in, centred over the
 *     page. The header (eyebrow, title, description, close) and the footer (the actions) stay put while
 *     the body scrolls between them, and under 40rem the panel takes the whole screen, so a phone still
 *     works. It enters with a short fade and scale — never a slide in from the side — and, like every
 *     kit animation, stands still under reduced motion (--clavian-motion-scale).
 *
 * Unsaved work is protected with `dirty`: a dismiss — Escape, the scrim, the close button — then asks
 * first, in a small alert dialog whose safe answer (Keep editing) takes focus.
 */
import * as RadixDialog from '@radix-ui/react-dialog';
import { useCallback, useRef, useState } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { usePortalContainer } from '../theme/theme-provider.js';
import { Button } from './button.js';
import { CloseIcon } from './icons.js';
/**
 * An in-place editor inside a sheet or dialog (a tag being typed, an inline edit) marks itself with
 * `data-owns-escape`, and Escape then cancels that editor rather than closing the layer around it.
 * Radix listens for Escape on the document in the capture phase, so the editor's own handler runs too
 * late to stop it with `stopPropagation`; the layer has to stand down itself.
 */
export function ownsEscape(event) {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-owns-escape]') !== null)
        event.preventDefault();
}
export function Dialog({ title, description, eyebrow, trigger, footer, size, open, defaultOpen, onOpenChange, dismissible = true, dirty = false, discardWords, onCloseAutoFocus, className, children, }) {
    const t = useUiText();
    const container = usePortalContainer();
    // Open or closed, whoever drives it: the caller (`open`) or the trigger (`defaultOpen`).
    const [uncontrolled, setUncontrolled] = useState(defaultOpen ?? false);
    const isOpen = open ?? uncontrolled;
    const setOpen = useCallback((next) => {
        if (open === undefined)
            setUncontrolled(next);
        onOpenChange?.(next);
    }, [open, onOpenChange]);
    // `dirty="auto"`: armed by the first edit inside the body, cleared by a submit or a close.
    const [touched, setTouched] = useState(false);
    const [confirming, setConfirming] = useState(false);
    // Closed (by anyone): forget the edit and the question, so the next open starts clean. Adjusted during
    // render on the change (React's "store the previous value" pattern), not in an effect.
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (wasOpen !== isOpen) {
        setWasOpen(isOpen);
        if (!isOpen) {
            setTouched(false);
            setConfirming(false);
        }
    }
    const unsaved = dirty === 'auto' ? touched : dirty;
    const requestOpenChange = useCallback((next) => {
        if (!next && unsaved) {
            setConfirming(true);
            return;
        }
        setOpen(next);
    }, [unsaved, setOpen]);
    // A submit hands the edit to the form's own save: if it fails, the next keystroke arms the guard again.
    const onEdit = dirty === 'auto' ? () => setTouched(true) : undefined;
    const onSubmit = dirty === 'auto' ? () => setTouched(false) : undefined;
    const framed = size !== undefined;
    const heading = (_jsxs("div", { className: "clav-dialog__heading", children: [eyebrow ? _jsx("p", { className: "clav-eyebrow", children: eyebrow }) : null, _jsx(RadixDialog.Title, { className: "clav-dialog__title", children: title }), description ? _jsx(RadixDialog.Description, { className: "clav-dialog__description", children: description }) : null] }));
    return (_jsxs(RadixDialog.Root, { open: isOpen, onOpenChange: requestOpenChange, children: [trigger ? _jsx(RadixDialog.Trigger, { asChild: true, children: trigger }) : null, _jsxs(RadixDialog.Portal, { container: container, children: [_jsx(RadixDialog.Overlay, { className: "clav-dialog__overlay" }), _jsxs(RadixDialog.Content, { className: cx('clav-dialog__content', framed && 'clav-dialog__content--framed', framed && `clav-dialog__content--${size}`, className), onEscapeKeyDown: dismissible ? ownsEscape : (event) => event.preventDefault(), onPointerDownOutside: dismissible ? undefined : (event) => event.preventDefault(), onCloseAutoFocus: onCloseAutoFocus, ...(description ? {} : { 'aria-describedby': undefined }), children: [_jsxs("div", { className: "clav-dialog__header", children: [heading, dismissible ? (_jsx(RadixDialog.Close, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", label: t('close'), icon: _jsx(CloseIcon, {}) }) })) : null] }), framed ? (_jsx("div", { className: "clav-dialog__body", tabIndex: 0, onInput: onEdit, onChange: onEdit, onSubmit: onSubmit, children: children })) : (children), footer ? _jsx("div", { className: "clav-dialog__footer", children: footer }) : null, confirming ? (_jsx(DiscardConfirm, { words: discardWords ?? { title: t('dialog.discardTitle'), body: t('dialog.discardBody'), keep: t('dialog.keepEditing'), discard: t('dialog.discard') }, onKeep: () => setConfirming(false), onDiscard: () => {
                                    setConfirming(false);
                                    setOpen(false);
                                } })) : null] })] })] }));
}
/**
 * "Discard your changes?" — the one confirm the kit stacks on a dialog, so it is the smallest one: an alert dialog
 * that must be answered. Its safe answer takes focus; Escape is that answer too; a click on the scrim does nothing.
 * It is rendered inside the dialog it guards, so Radix layers it — the dialog under it stays open and inert.
 */
function DiscardConfirm({ words, onKeep, onDiscard }) {
    const container = usePortalContainer();
    const keepRef = useRef(null);
    return (_jsx(RadixDialog.Root, { open: true, onOpenChange: (next) => (next ? undefined : onKeep()), children: _jsxs(RadixDialog.Portal, { container: container, children: [_jsx(RadixDialog.Overlay, { className: "clav-dialog__overlay clav-dialog__overlay--alert" }), _jsxs(RadixDialog.Content, { role: "alertdialog", className: "clav-dialog__content clav-dialog__content--alert", onPointerDownOutside: (event) => event.preventDefault(), onOpenAutoFocus: (event) => {
                        event.preventDefault();
                        keepRef.current?.focus();
                    }, children: [_jsxs("div", { className: "clav-dialog__heading", children: [_jsx(RadixDialog.Title, { className: "clav-dialog__title", children: words.title }), _jsx(RadixDialog.Description, { className: "clav-dialog__description", children: words.body })] }), _jsxs("div", { className: "clav-dialog__footer", children: [_jsx(Button, { variant: "ghost", onClick: onDiscard, children: words.discard }), _jsx(Button, { ref: keepRef, variant: "secondary", onClick: onKeep, children: words.keep })] })] })] }) }));
}
/** Closes the dialog it is inside — wrap a Button with it. */
export const DialogClose = RadixDialog.Close;
//# sourceMappingURL=dialog.js.map